import { useState, useRef, useEffect, useCallback } from 'react';
import {
  getReferralDocs,
  updateIntakeDocument,
  uploadDocumentToIntake,
  client,
} from '../services/api';
import { useToast } from './useToastContext';

interface DocumentInfo {
  id: string;
  fileName: string;
}

interface UseIntakeDocumentsOptions {
  intakeId: string | undefined;
  documents: Array<{ id: string }> | undefined;
  loadIntakeDetails: (intakeId: string) => Promise<unknown>;
}

/**
 * Custom hook to manage intake document operations including:
 * - Loading document URLs
 * - Document renaming
 * - Document uploading
 * - Refreshing presigned URLs
 */
export function useIntakeDocuments({
  intakeId,
  documents,
  loadIntakeDetails,
}: UseIntakeDocumentsOptions) {
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const hasFetchedDocumentUrlsRef = useRef<string | null>(null);

  const { error: showToastError, success: showToastSuccess } = useToast();

  // Load document URLs when intake is loaded (once per intake)
  useEffect(() => {
    const loadDocumentUrls = async () => {
      // Skip if already fetched for this intake
      if (hasFetchedDocumentUrlsRef.current === intakeId) return;
      if (!intakeId || !documents || documents.length === 0) return;

      hasFetchedDocumentUrlsRef.current = intakeId;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
          client,
        });

        if (response.data?.documents) {
          const urlMap: Record<string, string> = {};
          response.data.documents.forEach((doc: Record<string, string>) => {
            urlMap[doc.id] = doc.url;
          });
          setDocumentUrls(urlMap);
        }
      } catch (err) {
        console.error('Error loading document URLs:', err);
      }
    };

    loadDocumentUrls();
  }, [documents, intakeId]);

  // Set first document as active when documents change and no active document
  useEffect(() => {
    if (documents && documents.length > 0 && !activeDocumentId) {
      setActiveDocumentId(documents[0].id);
    }
  }, [documents, activeDocumentId]);

  // Handle document rename
  const handleDocumentRename = useCallback(
    async (documentId: string, fileName: string) => {
      try {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
          client,
        });
        showToastSuccess('Document renamed successfully.');
        // Refresh intake details to propagate changes
        if (intakeId) {
          await loadIntakeDetails(intakeId);
        }
      } catch (err) {
        console.error('Error renaming document:', err);
        showToastError('Failed to rename document.');
      }
    },
    [intakeId, loadIntakeDetails, showToastSuccess, showToastError]
  );

  // Handle document upload
  const handleDocumentAdd = useCallback(
    async (file: File, displayName: string): Promise<DocumentInfo | null> => {
      if (!intakeId) return null;

      try {
        const response = await uploadDocumentToIntake({
          path: { intake_id: intakeId },
          query: { display_name: displayName },
          body: { file },
          client,
        });

        if (response.data) {
          // Refresh intake to update selectedIntake.documents
          await loadIntakeDetails(intakeId);

          // Generate presigned URL for the new document
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeId },
            client,
          });

          if (docsResponse.data?.documents) {
            const urlMap: Record<string, string> = {};
            docsResponse.data.documents.forEach(
              (doc: Record<string, string>) => {
                urlMap[doc.id] = doc.url;
              }
            );
            setDocumentUrls(urlMap);
          }

          return {
            id: response.data.id,
            fileName: response.data.file_name,
          };
        }
        return null;
      } catch (err) {
        console.error('Error uploading document:', err);
        showToastError(
          'Upload failed',
          'Failed to upload document. Please try again.'
        );
        return null;
      }
    },
    [intakeId, loadIntakeDetails, showToastError]
  );

  /**
   * Handle refreshing document URL to get a fresh presigned URL.
   *
   * NOTE: This currently fetches ALL document URLs from the API and extracts the one we need.
   * This is a quick 80/20 fix. A more optimized approach would be to add a dedicated API
   * endpoint like GET /intake/{intake_id}/documents/{document_id}/url that returns only
   * the single document's presigned URL, reducing payload size and server load.
   */
  const handleRefreshDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      if (!intakeId) return null;

      try {
        const response = await getReferralDocs({
          path: { intake_id: intakeId },
          client,
        });

        if (response.data?.documents) {
          const doc = response.data.documents.find(
            (d: Record<string, string>) => d.id === documentId
          );
          if (doc) {
            // Update the documentUrls state with fresh URLs
            const urlMap: Record<string, string> = {};
            response.data.documents.forEach((d: Record<string, string>) => {
              urlMap[d.id] = d.url;
            });
            setDocumentUrls(urlMap);
            return doc.url;
          }
        }
        return null;
      } catch (err) {
        console.error('Error refreshing document URL:', err);
        return null;
      }
    },
    [intakeId]
  );

  return {
    documentUrls,
    activeDocumentId,
    setActiveDocumentId,
    handleDocumentRename,
    handleDocumentAdd,
    handleRefreshDocumentUrl,
  };
}
